import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { useSocket } from './SocketContext';
import { R } from '../utils/helpers';

const HRContext = createContext({});

function mapLeave(l) {
  return {
    id: l.req_id||l.id||'', emp: l.name||l.emp_name||l.emp||'', empId: l.emp_id||'',
    type: l.leave_type||l.type||'', from: l.from_date||l.from||'', to: l.to_date||l.to||'',
    days: l.days||0, reason: l.reason||'', status: l.status||'pending',
    mgrStatus: l.pm_status||l.status||'pending', hrStatus: l.hr_status||l.status||'pending',
    mgrComment: l.pm_comment||l.mgr_comment||'', hrComment: l.hr_comment||'',
    userId: l.user_id, userRole: l.user_role||'',
    pmName: l.pm_name||'', department: l.department||''
  };
}
function mapAdvance(a) {
  return {
    id: a.adv_id||a.id||'', emp: a.emp_name||a.emp||'', empId: a.emp_id||'',
    amt: a.amount ? R + Number(a.amount).toLocaleString('en-IN') : (a.amt||''),
    rawAmt: a.amount||0,
    type: a.type||'Full', emi: a.emi||'-', months: a.months||0,
    remaining: a.remaining||'', status: a.status||'pending',
    mgrStatus: a.mgr_status||a.mgrStatus||'pending',
    sentToMgr: a.sent_to_mgr||a.sentToMgr||false, reason: a.reason||'',
    userId: a.user_id,
    createdAt: a.requested_at||a.created_at||''
  };
}
function mapIntern(n) {
  // Derive display status from user_status + hr_interns.status
  const us = (n.user_status||'').toUpperCase();
  const hs = (n.status||'').toLowerCase();
  let displayStatus = hs;
  if (us === 'ONBOARDING_SUBMITTED') displayStatus = 'submitted';
  else if (us === 'TEMP') displayStatus = 'pending';
  else if (us === 'ACTIVE' || us === 'VERIFIED') displayStatus = 'active';
  else if (us === 'INACTIVE' && hs === 'Rejected') displayStatus = 'rejected';
  else if (hs === 'Rejected') displayStatus = 'rejected';
  return {
    id: n.intern_id||n.id||'', intern_id: n.intern_id||n.id||'',
    name: n.name||'', college: n.college||'',
    dept: n.dept||'', role: n.role_title||n.role||'', status: displayStatus,
    joined: n.start_date||n.joined||'', endDate: n.end_date||'',
    manager: n.manager||'', verified: n.verified||false,
    docs: n.doc_count||n.docs||0, total: n.total||11, userId: n.user_id,
    email: n.email||'', startDate: n.start_date||'',
    user_status: n.user_status||''
  };
}
function mapEmployee(e) {
  const us = (e.user_status||'').toUpperCase();
  const hs = (e.status||'').toLowerCase();
  let displayStatus = hs;
  if (us === 'ONBOARDING_SUBMITTED') displayStatus = 'submitted';
  else if (us === 'TEMP' && hs === 'onboarding') displayStatus = 'onboarding';
  else if (us === 'TEMP') displayStatus = 'pending';
  else if (us === 'ACTIVE' || us === 'VERIFIED') displayStatus = 'active';
  else if (us === 'INACTIVE' && hs === 'rejected') displayStatus = 'rejected';
  else if (hs === 'rejected') displayStatus = 'rejected';
  return {
    id: e.emp_id||e.id||'', emp_id: e.emp_id||e.id||'',
    name: e.name||'', dept: e.dept||'',
    role: e.role||'', status: displayStatus, joined: e.joined||'',
    userId: e.user_id, email: e.email||'',
    designation: e.designation||e.role||'', manager: e.manager||e.manager_name||'',
    docs: e.doc_count||e.docs||0, total: e.total||11,
    user_status: e.user_status||''
  };
}
function mapOffboard(o) {
  return {
    id: o.id || '', empId: o.emp_id || '', name: o.emp_name||o.name||'', dept: o.dept||'',
    resignDate: o.resign_date||o.resignDate||'', lastDay: o.exit_date||o.lastDay||'',
    status: o.status||'Pending', reason: o.reason||'',
    assetsReturned: !!o.it_cleared, userId: o.user_id
  };
}
function mapPayrollCycle(p) {
  return {
    step: p.stage||p.step||'', status: p.status==='completed'?'done':(p.status==='in_progress'?'active':'pending'),
    month: p.month||'', progress: p.progress||0
  };
}
function mapLog(g) {
  return {
    action: g.action||'', detail: g.detail||'', user: g.user_name||g.user||'',
    role: g.role||'', ts: g.timestamp_str||g.ts||''
  };
}

export function HRProvider({ children }) {
  const { connected, on, off } = useSocket();
  const [d, setD] = useState({
    loaded: false,
    kpiData: [], monthlyData: [], deptData: [],
    interns: [], hrEmployees: [],
    leaveReqs: [], payrollFlow: [],
    advances: [], auditLogs: [],
    announcements: [], offboardList: [],
  });

  // âœ… PREVENT INFINITE LOOPS - Add ref to track if data is fetched
  const fetchedRef = useRef(false);
  const inFlightRef = useRef(false);
  const queuedRefreshRef = useRef(false);
  const [loading, setLoading] = useState(false);

  // âœ… MEMOIZE fetch function to prevent recreating on each render
  const fetchHRData = useCallback(async (force = false) => {
    if (inFlightRef.current) {
      if (force) queuedRefreshRef.current = true;
      return;
    }

    // âœ… CRITICAL: Only fetch ONCE unless forced refresh is requested
    if (!force && fetchedRef.current) {
      console.log('â­ï¸ Skipping HR data fetch - already loaded');
      return;
    }

    try {
      inFlightRef.current = true;
      setLoading(true);
      console.log('ðŸ“Š Fetching HR data...');
      
      const [
        dash,
        interns,
        employees,
        leaves,
        payroll,
        advances,
        logs,
        announcements,
        offboard
      ] = await Promise.all([
        api.getHrDashboard().catch(() => ({})),
        api.getHrInterns().catch(() => []),
        api.getHrEmployees().catch(() => []),
        api.getHrLeaveRequests().catch(() => api.getAllLeaveRequests().catch(() => [])),
        api.getPayrollCycles().catch(() => []),
        api.getAdvances().catch(() => []),
        api.getAuditLogs().catch(() => []),
        api.getAnnouncements().catch(() => []),
        api.getOffboarding().catch(() => []),
      ]);

      const d2 = dash || {};
      
      setD({
        loaded: true,
        kpiData: d2.kpi || [],
        monthlyData: (d2.monthlyData || []).map(m => ({ 
          m: m.month || m.m, 
          emp: m.total_employees || m.emp, 
          cost: m.cost_lakhs || m.cost 
        })),
        deptData: d2.deptData || [],
        interns: (interns || []).map(mapIntern),
        hrEmployees: (employees || []).map(mapEmployee),
        leaveReqs: (leaves || []).map(mapLeave),
        payrollFlow: (payroll || []).map(mapPayrollCycle),
        advances: (advances || []).map(mapAdvance),
        auditLogs: (logs || []).map(mapLog),
        announcements: announcements || [],
        offboardList: (offboard || []).map(mapOffboard),
      });

      // âœ… Mark as fetched - prevents future calls
      fetchedRef.current = true;
      console.log('âœ… HR data loaded successfully');
      
    } catch (error) {
      console.error('âŒ Failed to fetch HR data:', error);
    } finally {
      inFlightRef.current = false;
      setLoading(false);
      if (queuedRefreshRef.current) {
        queuedRefreshRef.current = false;
        fetchHRData(true);
      }
    }
  }, []); // âœ… Empty dependency array - runs once

  // âœ… RUN ONLY ONCE when component mounts
  useEffect(() => {
    fetchHRData();
  }, [fetchHRData]); // âœ… Only depends on memoized fetch function

  // âœ… MANUAL REFRESH FUNCTION (call this when you need to refresh)
  const refreshHRData = useCallback(async () => {
    await fetchHRData(true);
  }, [fetchHRData]);

  // Realtime refresh for HR dashboards
  useEffect(() => {
    if (!connected) return;
    const handle = () => { refreshHRData().catch(() => {}); };
    on('notification', handle);
    on('notification:new', handle);
    on('helpdesk:update', handle);
    on('leave:update', handle);
    on('attendance:update', handle);
    on('payroll:update', handle);
    on('employee:update', handle);
    on('intern:update', handle);
    on('policy:update', handle);
    on('offboarding:update', handle);
    return () => {
      off('notification', handle);
      off('notification:new', handle);
      off('helpdesk:update', handle);
      off('leave:update', handle);
      off('attendance:update', handle);
      off('payroll:update', handle);
      off('employee:update', handle);
      off('intern:update', handle);
      off('policy:update', handle);
      off('offboarding:update', handle);
    };
  }, [connected, on, off, refreshHRData]);

  return (
    <HRContext.Provider value={{ 
      ...d, 
      loading, 
      refreshHRData,
      refreshInterns: refreshHRData,
      refreshEmployees: refreshHRData
    }}>
      {children}
    </HRContext.Provider>
  );
}

export const useHRData = () => useContext(HRContext);

